import { AsyncPipe } from '@angular/common';
import { Component, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormsModule, UntypedFormGroup } from '@angular/forms';
import { DeviceParameter, MotionMasterMessage } from 'motion-master-client';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { systemIdentificationCompleted } from '@app/models/status-resolve';
import { DeviceService } from '@app/services/device.service';
import { ModalService } from '@app/services/modal.service';
import { SettingService } from '@app/services/setting.service';
import { BetaBadgeComponent } from '@app/shared/beta-badge/beta-badge.component';
import { DeviceBaseComponent } from '@app/shared/device-base.component';
import { IsPdoControlModeDirective } from '@app/shared/is-pdo-control-mode.directive';
import { ParameterFormComponent } from '@app/shared/parameter-form/parameter-form.component';
import { PlantModelComponent } from '@app/shared/plant-model/plant-model.component';
import { ToggleVisibilityButtonComponent } from '@app/shared/toggle-visibility-button/toggle-visibility-button.component';
import { Device } from '@app/store/device.model';
import { mapParametersToFieldNameValues, Parameter } from '@app/store/parameter.model';
import { systemIdentificationParameterId } from '@app/store/system-identification-parameter.model';
import { getCurrentValue } from '@app/util';

@Component({
  selector: 'app-system-identification',
  templateUrl: './system-identification.component.html',
  imports: [
    ToggleVisibilityButtonComponent,
    PlantModelComponent,
    ParameterFormComponent,
    IsPdoControlModeDirective,
    BetaBadgeComponent,
    FormsModule,
    AsyncPipe,
  ],
})
export class SystemIdentificationComponent extends DeviceBaseComponent implements OnDestroy {
  private deviceService = inject(DeviceService);
  private settingService = inject(SettingService);
  private modalService = inject(ModalService);
  private toastr = inject(ToastrService);

  @Input() showDescription = true;
  @Input() submitText = 'RUN SYSTEM IDENTIFICATION';

  @ViewChild('lrsiForm') lrsiForm: ParameterFormComponent;

  expertMode$ = this.settingService.expertMode$;

  hasPlantModel$: Observable<boolean>;
  hasSavedPlantValues$: Observable<boolean>;
  canOpenBodePlot = false;
  running$: Observable<boolean>;
  parameters$: Observable<Parameter[]>;
  lrsiParameters$: Observable<Parameter[]>;
  torqueOffset$: Observable<DeviceParameter[]>;

  filterActive = true;
  filterParameters$ = new BehaviorSubject<boolean | void>(this.filterActive);

  bodeFileContent: string;

  hasFault$: Observable<boolean>;
  safeStateActive$: Observable<boolean>;

  switchedOn$: Observable<boolean>;
  operationEnabled$: Observable<boolean>;
  quickStopActive$: Observable<boolean>;

  isMotionPrevented$: Observable<boolean>;
  motionPreventedMessage$: Observable<string>;

  subscriptions = new Subscription();

  limitedRangeSysIdEnabled: boolean = false;
  rangeLimit: number = 1000000;

  starting = false;

  get advancedToggleButtonVisible() {
    return getCurrentValue(this.expertMode$) === false && this.componentMode === 'setup';
  }

  get torqueOffsetFormVisible() {
    return getCurrentValue(this.expertMode$) && this.componentMode === 'setup';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onDeviceChange(device: Device) {
    this.running$ = this.deviceService.selectDeviceSystemIdentificationRunning(device);
    this.parameters$ = this.filterParameters$.pipe(
      switchMap(() => this.deviceService.selectDeviceSystemIdentificationParameters(device)),
    );
    this.lrsiParameters$ = this.deviceService.selectDeviceLimitedRangeSystemIdentificationParameters(device);

    this.torqueOffset$ = this.deviceService.selectDeviceParameters(device, [[0x60b2, 0]]);

    this.hasFault$ = this.deviceService.selectDeviceHasFault(device);
    this.safeStateActive$ = this.deviceService.selectDeviceSafeStateActive(device);

    this.hasPlantModel$ = this.deviceService.selectDeviceHasPlantModel(device);
    this.switchedOn$ = this.deviceService.selectDeviceSwitchedOn(device);
    this.operationEnabled$ = this.deviceService.selectDeviceOperationEnabled(device);
    this.quickStopActive$ = this.deviceService.selectDeviceQuickStopActive(device);

    this.isMotionPrevented$ = this.deviceService.selectIsMotionPrevented(device);
    this.motionPreventedMessage$ = this.deviceService.selectMotionPreventedMessage(device);

    this.hasSavedPlantValues$ = this.deviceService.selectCurrentDevicePlantModelHasSavedChirpSignalValues();

    this.subscriptions.add(
      this.deviceService
        .selectDeviceProperty(device, 'systemIdentificationStatus')
        .pipe(filter((status) => systemIdentificationCompleted(status)))
        .subscribe((status) => {
          // Motion Master provides the Bode file content (including the measured FRF) for all
          // firmware versions — for firmware < 5.6.0 it computes the chirp on the host instead of
          // using the on-drive OS command — so capturing it is no longer gated on firmware >= 5.6.0.
          // @ts-ignore-error
          this.bodeFileContent = status.bodeFileContent || '';
          this.canOpenBodePlot = !!this.bodeFileContent;
        }),
    );

    this.subscriptions.add(
      combineLatest([this.deviceService.selectDeviceParameterValue(device, 'maxTorque'), this.expertMode$]).subscribe(
        ([maxTorque, expertMode]) => {
          this.deviceService.updateDeviceSystemIdentificationMaxTorqueAmplitudeValue(expertMode, maxTorque);
        },
      ),
    );

    this.subscriptions.add(
      this.deviceService.selectDeviceOperationEnabled(device).subscribe((operationEnabled) => {
        if (operationEnabled) {
          this.starting = false;
        }
      }),
    );

    this.subscriptions.add(
      this.deviceService.selectDeviceSystemIdentificationEnded(device).subscribe(() => {
        this.starting = false;
      }),
    );

    this.subscriptions.add(
      this.hasPlantModel$.subscribe((hasPlantModel) => {
        this.canOpenBodePlot = hasPlantModel;
      }),
    );

    const hasPlantModel = getCurrentValue(this.hasPlantModel$);
    if (!hasPlantModel) {
      this.deviceService.requestLoadDeviceFile(device, 'plant_model.csv', true, true, 5000);
    }

    /**
     * When the component is initialized, if a plant model is already available, update the system identification
     * parameters with the values from the plant model. This ensures that the user has the correct parameter values
     * in the form.
     */
    this.hasPlantModel$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.deviceService.dispatchUpdateSystemIdentificationParametersFromPlantModel(this.deviceService.currentDevice);
    });
  }

  onTorqueOffsetSubmit(formControl: FormControl) {
    this.deviceService.requestSetDeviceParameterValues(formControl);
  }

  onFormSubmit(formGroup: UntypedFormGroup) {
    this.deviceService.updateDeviceSystemIdentificationParameterValues(formGroup.value);

    const parameters = getCurrentValue(this.parameters$);
    const values = mapParametersToFieldNameValues<MotionMasterMessage.Request.IStartSystemIdentification>(parameters);

    this.starting = true;

    if (this.limitedRangeSysIdEnabled) {
      this.deviceService.updateDeviceLimitedRangeSystemIdentificationParameterValues(this.lrsiForm.form.value);
      const lrsiParameters = getCurrentValue(this.lrsiParameters$);
      const lrsiValues = mapParametersToFieldNameValues(lrsiParameters);

      this.deviceService.requestStartLimitedRangeSystemIdentification(
        this.device,
        values.durationSeconds,
        values.torqueAmplitude,
        values.startFrequency,
        values.endFrequency,
        lrsiValues.rangeLimit,
      );
    } else {
      this.deviceService.requestStartSystemIdentification(
        this.device,
        values.durationSeconds,
        values.torqueAmplitude,
        values.startFrequency,
        values.endFrequency,
      );
    }
  }

  onStop() {
    this.deviceService.requestStopDevice(this.device);
  }

  onDisableMotionController() {
    this.deviceService.requestDisableMotionController(this.device);
  }

  onToggleFilterButton(active: boolean) {
    this.filterActive = active;
    this.filterParameters$.next();
  }

  openBodePlotModal() {
    if (this.bodeFileContent?.length) {
      this.modalService.openBodePlotModal(this.device, this.bodeFileContent);
      return;
    }

    this.deviceService
      .getPlantModelBodeFileContent(this.device.serialNumber)
      .pipe(take(1))
      .subscribe({
        next: (bodeFileContent) => {
          this.canOpenBodePlot = true;
          this.modalService.openBodePlotModal(this.device, bodeFileContent);
        },
        error: (_err) => {
          this.toastr.error(
            'Failed to load Bode plot data from the device. Please run the system identification procedure.',
            'Bode Plot',
            { disableTimeOut: true },
          );
          this.canOpenBodePlot = false;
        },
      });
  }

  openLimitedRangeSysIdModal() {
    this.modalService.openDescriptionModal(
      'Limited Range System Identification',
      `
      <p>
        This feature allows to carry out the system identification procedure while respecting user-defined position limits, even under the influence of gravity.
      </p>
      <p>
        Use this feature in situations where loads are prone to fall without velocity or position control.
      </p>
      <p>
        Typical examples are unbalanced loads and robot links.
      </p>
      <p>
        This feature can also be used when system identification must be done while strict position limits have to be respected.
      </p>
    `,
    );
  }

  onLimitedRangeSysIdChange() {
    const currentTorqueExcitationSignalAmplitudeParameter = getCurrentValue(
      this.deviceService.selectDeviceSystemIdentificationParameter(this.device, 'torqueAmplitude'),
    );

    const currentTorqueExcitationSignalAmplitudeParameterId = systemIdentificationParameterId(
      'torqueAmplitude',
      this.device,
    );

    if (!currentTorqueExcitationSignalAmplitudeParameter) {
      return;
    }

    if (this.limitedRangeSysIdEnabled) {
      this.deviceService.updateDeviceSystemIdentificationParameterValues({
        [currentTorqueExcitationSignalAmplitudeParameterId]: currentTorqueExcitationSignalAmplitudeParameter.min,
      });
    } else {
      this.deviceService.updateDeviceSystemIdentificationParameterValues({
        [currentTorqueExcitationSignalAmplitudeParameterId]:
          currentTorqueExcitationSignalAmplitudeParameter.defaultData,
      });
    }
  }
}
